import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select, { type SingleValue } from 'react-select';
import { Calendar, ChevronRight, Download, FileSpreadsheet, Loader2, Play, Users } from 'lucide-react';
import { employeeApi, payRecordApi, paymentFrequencyApi, paymentTypeApi, timecardApi } from '../services/api';
import { useWeekStartDay } from '../hooks/useWeekStartDay';
import { WeekPickerCalendar } from '../components/payroll/WeekPickerCalendar';
import { formatRangeDate, parseLocalDate, toLocalDateString } from '../utils/payrollPeriodLocal';
import {
  buildStaffSalariedTable,
  isWeeklyOrMonthlySalaried,
  staffSalariedRowsToGrid,
  type StaffSalariedReportRow,
} from '../utils/staffSalariedReport';
import { createCSVFile, createExcelFile } from '../utils/reportExportFiles';
import type { Employee, PayRecord } from '../types';

type EmpOption = { value: number; label: string };

const StaffSalariedTableReport: React.FC = () => {
  const weekStartsOn = useWeekStartDay();

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [payRecords, setPayRecords] = useState<PayRecord[]>([]);
  const [paymentTypeNameById, setPaymentTypeNameById] = useState<Map<number, string>>(new Map());
  const [paymentFreqNameById, setPaymentFreqNameById] = useState<Map<number, string>>(new Map());
  const [masterLoading, setMasterLoading] = useState(true);

  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [fromWeek, setFromWeek] = useState('');
  const [toWeek, setToWeek] = useState('');
  const [fromPickerOpen, setFromPickerOpen] = useState(false);
  const [toPickerOpen, setToPickerOpen] = useState(false);

  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [displayRows, setDisplayRows] = useState<StaffSalariedReportRow[]>([]);
  const [ranOnce, setRanOnce] = useState(false);

  const getWeekStart = useCallback(
    (date: Date): string => {
      const day = date.getDay();
      let diff = day - weekStartsOn;
      if (diff < 0) diff += 7;
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - diff);
      return toLocalDateString(weekStart);
    },
    [weekStartsOn]
  );

  useEffect(() => {
    const load = async () => {
      setMasterLoading(true);
      try {
        const [emps, records, types, freqs] = await Promise.all([
          employeeApi.getAllFull(),
          payRecordApi.getAll(),
          paymentTypeApi.getAll(),
          paymentFrequencyApi.getAll(),
        ]);
        setEmployees(emps || []);
        setPayRecords(records || []);
        const tm = new Map<number, string>();
        (types || []).forEach((t) => tm.set(t.id, t.name));
        setPaymentTypeNameById(tm);
        const fm = new Map<number, string>();
        (freqs || []).forEach((f) => fm.set(f.id, f.name));
        setPaymentFreqNameById(fm);

        const today = new Date();
        setToWeek(getWeekStart(today));
        const f = new Date(today);
        f.setDate(f.getDate() - 28);
        setFromWeek(getWeekStart(f));
      } catch (e) {
        console.error(e);
      } finally {
        setMasterLoading(false);
      }
    };
    void load();
  }, [getWeekStart]);

  const salariedOptions = useMemo((): EmpOption[] => {
    const out: EmpOption[] = [];
    for (const e of employees) {
      if (!e.isActive) continue;
      const activePr = payRecords.find((p) => p.employeeId === e.id && p.isActive);
      if (!isWeeklyOrMonthlySalaried(activePr, paymentTypeNameById, paymentFreqNameById)) continue;
      out.push({
        value: e.id,
        label: `${e.firstName} ${e.lastName}`.trim(),
      });
    }
    out.sort((a, b) => a.label.localeCompare(b.label));
    return out;
  }, [employees, payRecords, paymentTypeNameById, paymentFreqNameById]);

  const selectedOption = useMemo(() => {
    if (selectedEmployeeId == null) return null;
    return salariedOptions.find((o) => o.value === selectedEmployeeId) ?? null;
  }, [selectedEmployeeId, salariedOptions]);

  const fromWeekLabel = useMemo(() => {
    if (!fromWeek) return '';
    const start = parseLocalDate(fromWeek);
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
    return `${formatRangeDate(start)} - ${formatRangeDate(end)}`;
  }, [fromWeek]);

  const toWeekLabel = useMemo(() => {
    if (!toWeek) return '';
    const start = parseLocalDate(toWeek);
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
    return `${formatRangeDate(start)} - ${formatRangeDate(end)}`;
  }, [toWeek]);

  const handleRun = useCallback(async () => {
    setError(null);
    if (selectedEmployeeId == null) {
      setError('Select an employee.');
      return;
    }
    if (!fromWeek || !toWeek) {
      setError('Select From week and To week.');
      return;
    }
    if (fromWeek > toWeek) {
      setError('From week must be on or before To week.');
      return;
    }

    setRunning(true);
    setDisplayRows([]);
    try {
      const emp = employees.find((e) => e.id === selectedEmployeeId);
      const name = emp ? `${emp.firstName} ${emp.lastName}`.trim() : `Employee #${selectedEmployeeId}`;

      const [cards, empPayRecords] = await Promise.all([
        timecardApi.getAll(selectedEmployeeId),
        payRecordApi.getByEmployeeId(selectedEmployeeId),
      ]);

      const { displayRows: rows } = buildStaffSalariedTable(name, cards, empPayRecords || [], fromWeek, toWeek);
      setDisplayRows(rows);
      setRanOnce(true);
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Failed to build report.');
    } finally {
      setRunning(false);
    }
  }, [selectedEmployeeId, fromWeek, toWeek, employees]);

  const handleExport = useCallback(
    (kind: 'csv' | 'excel') => {
      if (displayRows.length === 0) return;
      const { headers, stringRows } = staffSalariedRowsToGrid(displayRows);
      const base = `Staff_Salaried_Table_${fromWeek}_${toWeek}`;
      if (kind === 'csv') {
        const f = createCSVFile(headers, stringRows, base);
        const a = document.createElement('a');
        a.href = f.url;
        a.download = f.filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(f.url);
      } else {
        const f = createExcelFile(headers, stringRows, base);
        const a = document.createElement('a');
        a.href = f.url;
        a.download = f.filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(f.url);
      }
    },
    [displayRows, fromWeek, toWeek]
  );

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-7xl">
      <div>
        <h2 className="text-xl font-bold text-neutral-900 flex items-center gap-2">
          <Users className="h-6 w-6 text-neutral-600" />
          Staff Salaried table
        </h2>
        <p className="text-sm text-neutral-600 mt-1">
          Weekly or monthly salaried employees only. Hours worked come from approved timecards (sum of day totals). Set
          against hours is the contracted hours on the pay record for that week. Hours in lieu due is a running total of
          hours under/over from the employee&apos;s first week of data through each row; only weeks in your selected
          range are shown.
        </p>
      </div>

      <div className="bg-white rounded-lg border border-neutral-200 p-4 space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4 items-end">
          <div className="space-y-1 min-w-0 lg:col-span-2">
            <label className="block text-sm font-medium text-neutral-700">Employee</label>
            <Select<EmpOption, false>
              classNamePrefix="staff-salaried-emp"
              placeholder={masterLoading ? 'Loading…' : 'Search or select…'}
              isLoading={masterLoading}
              isClearable
              options={salariedOptions}
              value={selectedOption}
              onChange={(opt: SingleValue<EmpOption>) => setSelectedEmployeeId(opt?.value ?? null)}
              noOptionsMessage={() => 'No salaried employees match'}
              filterOption={(option, raw) => {
                const q = raw.toLowerCase();
                return option.label.toLowerCase().includes(q);
              }}
              styles={{
                control: (base) => ({ ...base, minHeight: 42, borderColor: '#d4d4d4', fontSize: 14 }),
                menuPortal: (base) => ({ ...base, zIndex: 50 }),
              }}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            />
          </div>

          <div className="space-y-1 min-w-0">
            <label className="block text-sm font-medium text-neutral-700">
              <Calendar className="inline h-4 w-4 mr-1 align-text-bottom" />
              From week
            </label>
            {fromPickerOpen ? (
              <WeekPickerCalendar
                value={fromWeek}
                onChange={(ymd) => setFromWeek(ymd)}
                onClose={() => setFromPickerOpen(false)}
                weekStartsOn={weekStartsOn}
              />
            ) : (
              <button
                type="button"
                onClick={() => setFromPickerOpen(true)}
                className="flex w-full max-w-sm items-center gap-1.5 px-3 py-2 border border-neutral-300 rounded-md bg-white hover:bg-neutral-50 text-left text-sm"
              >
                <Calendar className="h-4 w-4 text-neutral-500 shrink-0" />
                <span className={fromWeekLabel ? 'text-neutral-800' : 'text-neutral-500'}>
                  {fromWeekLabel || 'Select week'}
                </span>
                <ChevronRight className="h-4 w-4 text-neutral-400 ml-auto shrink-0" />
              </button>
            )}
          </div>

          <div className="space-y-1 min-w-0">
            <label className="block text-sm font-medium text-neutral-700">
              <Calendar className="inline h-4 w-4 mr-1 align-text-bottom" />
              To week
            </label>
            {toPickerOpen ? (
              <WeekPickerCalendar
                value={toWeek}
                onChange={(ymd) => setToWeek(ymd)}
                onClose={() => setToPickerOpen(false)}
                weekStartsOn={weekStartsOn}
              />
            ) : (
              <button
                type="button"
                onClick={() => setToPickerOpen(true)}
                className="flex w-full max-w-sm items-center gap-1.5 px-3 py-2 border border-neutral-300 rounded-md bg-white hover:bg-neutral-50 text-left text-sm"
              >
                <Calendar className="h-4 w-4 text-neutral-500 shrink-0" />
                <span className={toWeekLabel ? 'text-neutral-800' : 'text-neutral-500'}>
                  {toWeekLabel || 'Select week'}
                </span>
                <ChevronRight className="h-4 w-4 text-neutral-400 ml-auto shrink-0" />
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => void handleRun()}
            disabled={running || masterLoading || selectedEmployeeId == null}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run report
          </button>
          <button
            type="button"
            onClick={() => handleExport('csv')}
            disabled={displayRows.length === 0}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-800 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            CSV
          </button>
          <button
            type="button"
            onClick={() => handleExport('excel')}
            disabled={displayRows.length === 0}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-800 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 disabled:opacity-50"
          >
            <FileSpreadsheet className="h-4 w-4" />
            Excel
          </button>
        </div>

        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>
        )}
      </div>

      {displayRows.length > 0 && (
        <div className="overflow-x-auto border border-neutral-200 rounded-lg bg-white">
          <table className="w-full text-sm text-left">
            <thead className="bg-neutral-50 border-b border-neutral-200 text-neutral-600">
              <tr>
                <th className="px-3 py-2 whitespace-nowrap">Employee</th>
                <th className="px-3 py-2 text-right">Year</th>
                <th className="px-3 py-2 text-right">Week no.</th>
                <th className="px-3 py-2 text-right">Hours worked</th>
                <th className="px-3 py-2 text-right">Set against hours</th>
                <th className="px-3 py-2 text-right">Hours under/over</th>
                <th className="px-3 py-2 text-right">Hours in lieu due</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((r) => (
                <tr key={r.weekStartDate} className="border-b border-neutral-100">
                  <td className="px-3 py-2 whitespace-nowrap">{r.employeeDisplay}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.yearNo}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.weekNo}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.hoursWorked.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.setAgainstHours.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.hoursUnderOver.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{r.hoursInLieuDue.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {ranOnce && displayRows.length === 0 && !error && !running && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          No approved timecard weeks in the selected range. Lieu balances still include every approved week (before the
          range) when computing rows that do appear.
        </p>
      )}
      {!ranOnce && !running && displayRows.length === 0 && !error && (
        <p className="text-sm text-neutral-500 border border-dashed border-neutral-200 rounded-lg px-4 py-6 text-center">
          Choose an employee and week range, then run the report.
        </p>
      )}
    </div>
  );
};

export default StaffSalariedTableReport;
